import React, { useState, useEffect, useMemo, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { BsSearch } from 'react-icons/bs';
import { Modal, DatePicker, Select, Input } from 'antd';
import { Form, FormProps } from 'ALIASED-antd';
import * as actions from '@actions';
import Datestamp from 'vl-common/src/components/Datestamp';
import addBusinessDays from 'date-fns/addBusinessDays';
import moment from 'moment';
import useUser from '@vl-core/hooks/useUser';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';

const { Option } = Select;

const formOptions: FormProps = {
  labelWrap: true,
  labelCol: { span: 8 },
  wrapperCol: { span: 16 }
};

export const CreateF2FTaskModal = ({
  open,
  onClose,
  ...otherProps
}: { onClose: () => void | Promise<void> } & ComponentProps<typeof Modal>) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const user = useUser();
  const { appointment } = useSessionAppointment();
  const [loading, setLoading] = useState(false);
  const [completed, setf2fComplete] = useSessionFormState('f2fComplete');
  const [reason] = useSessionFormState('ReasonForReferral');
  const [speciality] = useSessionFormState('speciality');
  const [subSpeciality] = useSessionFormState('subspeciality');
  const [refClin] = useSessionFormState('refcli');

  const taskData = {
    speciality,
    subSpeciality,
    refClin,
    reason
  };

  const [dueDate, setDueDate] = useState();
  const [notes, setNotes] = useState('');
  const [apptType, setApptType] = useState('');
  const defaultDateValue = useMemo(() => moment(addBusinessDays(new Date(), 2)), []);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        const res = await dispatch(
          actions.clinicianAllTasks({ task_type: 'F2F-SPEC', client_case_id: appointment.client_case_id }, true)
        );
        if (res?.payload[0]) {
          setDueDate(res.payload[0].due_date);
          setNotes(res.payload[0].task_notes);
        }
      };
      fetchData().then();
    }
  }, [open, appointment?.client_case_id, dispatch]);

  const onFinish = async (data: { description: string }) => {
    setLoading(true);
    const initValue = {
      user_guid: user.user_guid,
      appt_id: appointment.appointment_id,
      refclin_guid: taskData.refClin ? taskData.refClin.clin_user_guid : '',
      speccat: String(taskData.speciality),
      specarea: taskData.subSpeciality,
      hospital_id: taskData.refClin ? taskData.refClin.hospital_id : undefined,
      reason: taskData.reason,
      postcode: taskData.refClin ? taskData.refClin.postcode : ''
    };

    const Values = {
      ...initValue,
      patient_guid: appointment.patient_user_guid,
      case_id: appointment.case_id,
      task_type_code: 'F2F-SPEC',
      appt_type_code: apptType,
      description: data.description,
      affected_user_guid: '',
      assignee_user_guid: ''
    };

    try {
      if (taskData.speciality) {
        await dispatch(actions.postReferral(initValue));
      }
      const res = await dispatch(actions.taskCreate(Values));
      if (res?.task_id) {
        setf2fComplete(true);
        sessionStorage.removeItem(`f2f-referral-${appointment.appointment_id}`);

        onClose();

        form.resetFields();
      }
    } catch (error) {
      console.log('error', error);
    }
    setLoading(false);
  };

  const renderTemplate = () => (
    <Form {...formOptions} name="create_f2f_task" form={form} onFinish={onFinish}>
      <Form.Item label={t('Appointment Type Requested')}>
        {completed ? (
          <div>{t('Consultant')}</div>
        ) : (
          <Select
            placeholder={t('Select Type')}
            defaultValue="CONSULTANT"
            value={apptType}
            onChange={(value) => setApptType(value)}
            data-testid="appointment-type-select"
          >
            {/* {metaData && metaData?.task_types && metaData?.task_types?.find((item) => item.type_code === 'F2F-SPEC').task_sub_types?.map((res, index) => <Option key={index} value={res.task_sub_type_code}>{res.task_sub_type}</Option>)} */}
            <Option value="CONSULTANT">{t('Consultant')}</Option>
          </Select>
        )}
      </Form.Item>

      <Form.Item label={t('Patient')} required>
        <BsSearch aria-hidden css={{ color: '#e0e0e0', marginRight: 5 }} />
        {appointment.patient}
      </Form.Item>

      {taskData.refClin && (
        <Form.Item label={t('Recommended clinician')}>
          <p css={{ fontSize: '0.9em', marginTop: '0.5em' }}>
            {taskData.refClin.title} {taskData.refClin.first_name} {taskData.refClin.last_name}
            <br />
            <strong>{t('Speciality: ')}</strong> {taskData.refClin.cat_name}
            <br />
            <strong>{t('Sub Speciality: ')}</strong> {taskData.refClin.spec_areas}
            <br />
            <strong>{t('Hospital: ')}</strong> {taskData.refClin.hospital_name} ({taskData.refClin.postcode})
          </p>
          <small>{t('It may not be possible to book an appointment with your recommended clinician.')}</small>
        </Form.Item>
      )}

      {defaultDateValue && (
        <Form.Item name="due_date" label={t('Due Date')} required initialValue={defaultDateValue}>
          {completed && dueDate ? (
            <Datestamp date={dueDate} format="Date only (localised) - 03/02/2022" />
          ) : (
            <DatePicker format="DD/MM/YYYY" css={{ width: '100%' }} />
          )}
        </Form.Item>
      )}

      <Form.Item
        name="description"
        rules={[{ required: true, message: 'Please enter the notes!' }]}
        label={t('Notes')}
        help="When is the patient not available during the next two weeks?"
      >
        {completed ? notes : <Input.TextArea rows={4} />}
      </Form.Item>
    </Form>
  );

  return (
    <Modal
      title={completed ? 'F2F task' : 'Create a F2F task'}
      width={750}
      open={open}
      onOk={onClose}
      okText="Create"
      okButtonProps={{
        'data-testid': 'create',
        disabled: !!completed || loading,
        htmlType: 'submit',
        form: 'create_f2f_task'
      }}
      onCancel={onClose}
      cancelButtonProps={{ 'data-testid': 'cancel', disabled: !!completed }}
      confirmLoading={loading}
      {...otherProps}
    >
      {renderTemplate()}
    </Modal>
  );
};
